import React from 'react';
import { toast } from 'react-toastify';

// The only payment item states an OCS may be generated with (i.e. not DRAFT).
const ALLOWED_STATUSES = ['GENERATED', 'PARTIALLY_GENERATED', 'OVERDUE_GENERATED', 'SENT', 'PAID', 'PARTIALLY_PAID', 'OVERDUE'];
// Reversed invoices: they neither count towards the minimums nor block the OCS, but if they
// were issued a serial they need a credit note (checked below).
const REVERSED_STATUSES = ['CANCELLED', 'REFUNDED', 'PARTIALLY_REFUNDED', 'TO_BE_REFUNDED'];

// GST counts as assigned once the invoice has been issued a GST serial number.
const hasGstSerialNumber = (item) => item?.serialNumber != null && item.serialNumber !== '';

const toArray = (paymentItems) => {
    if (!paymentItems) return [];
    return Array.isArray(paymentItems) ? paymentItems : Object.values(paymentItems);
};

/**
 * Everything that currently stops an OCS from being generated for this order.
 * Returns a list of human-readable reasons; empty means the OCS is allowed.
 */
export const getOcsBlockers = (orderData) => {
    const blockers = [];
    const paymentItems = toArray(orderData?.paymentItems).filter(
        (item) => item?.documentType !== 'CREDIT_NOTE' && !REVERSED_STATUSES.includes(item?.status)
    );
    const serviceItems = paymentItems.filter((item) => item.invoiceType === 'SERVICE');
    const medicineItems = paymentItems.filter((item) => item.invoiceType === 'MEDICINE');

    if (!serviceItems.length) blockers.push('Add at least 1 service payment item');
    if (!medicineItems.length) blockers.push('Add at least 1 medicine payment item');

    // Only MEDICINE items may go without a GST serial number.
    const withoutSerial = paymentItems.filter((item) => item.invoiceType !== 'MEDICINE' && !hasGstSerialNumber(item));
    if (withoutSerial.length) {
        blockers.push(`GST serial number not assigned to payment item(s): ${withoutSerial.map((i) => `#${i.id} (${i.invoiceType})`).join(', ')}`);
    }

    const unsettled = paymentItems.filter((item) => !ALLOWED_STATUSES.includes(item.status));
    if (unsettled.length) {
        blockers.push(`Payment item(s) must be generated, sent, overdue or paid: ${unsettled.map((i) => `#${i.id} (${i.status})`).join(', ')}`);
    }

    // An issued invoice that was cancelled / refunded must be reversed by a credit note,
    // and every credit note must carry its own GST serial number.
    const allItems = toArray(orderData?.paymentItems);
    const creditNotes = allItems.filter((item) => item?.documentType === 'CREDIT_NOTE' && item.status !== 'CANCELLED');
    const reversedWithoutCreditNote = allItems.filter(
        (item) => item?.documentType !== 'CREDIT_NOTE'
            && item?.invoiceType !== 'MEDICINE'
            && REVERSED_STATUSES.includes(item.status)
            && hasGstSerialNumber(item)
            && !creditNotes.some((cn) => String(cn.originalPaymentItemId) === String(item.id))
    );
    if (reversedWithoutCreditNote.length) {
        blockers.push(`Credit note not created for payment item(s): ${reversedWithoutCreditNote.map((i) => `#${i.id} (${i.status})`).join(', ')}`);
    }
    const creditNotesWithoutSerial = creditNotes.filter((cn) => !hasGstSerialNumber(cn));
    if (creditNotesWithoutSerial.length) {
        blockers.push(`Serial number not assigned to credit note(s): ${creditNotesWithoutSerial.map((i) => `#${i.id}`).join(', ')}`);
    }

    const addressId = parseInt(orderData?.addressId, 10);
    if (!(addressId > 0) || !orderData?.address) blockers.push('Address is missing on the order');

    if (!orderData?.inventoryId) blockers.push('Evital is not mapped to the order (no inventory ID)');
    if (!orderData?.additionalDetails?.billNumber) {
        blockers.push(
            orderData?.inventoryId
                ? 'Evital bill number is missing -- use "Fetch Bill Number from Evital"'
                : 'Evital bill number is missing'
        );
    }

    return blockers;
};

/**
 * Toasts every blocker and returns false when the OCS must not be generated.
 * Pass `bypass: true` (hidden escape hatch: the order ID tapped 10 times on the edit page)
 * to skip every rule above.
 */
export const checkOcsReadiness = (orderData, bypass = false) => {
    const toastId = `ocs-blockers-${orderData?.id}`;
    if (bypass) {
        toast.dismiss(toastId);
        toast.warn('OCS readiness checks bypassed');
        return true;
    }
    const blockers = getOcsBlockers(orderData);
    if (!blockers.length) return true;

    toast.error(
        <div>
            <strong>Cannot generate OCS. Fix the following:</strong>
            <ul style={{ listStyle: 'disc', paddingLeft: '1.2rem', marginTop: '0.25rem' }}>
                {blockers.map((blocker) => <li key={blocker}>{blocker}</li>)}
            </ul>
        </div>,
        { autoClose: 10000, toastId }
    );
    return false;
};
